import { Show, createEffect, onCleanup, type JSX } from "solid-js";
import { Portal } from "solid-js/web";
import { IconButton } from "./Form.tsx";
import { CloseIcon } from "./icons.tsx";
import { useI18n } from "../i18n/provider.tsx";
import styles from "./Modal.module.scss";

export type ModalProps = {
  /** Nothing renders while this is false, so callers can bind it to a signal. */
  open: boolean;
  title: string;
  /** The subject the dialog is about, such as the id being edited. */
  description?: string;
  /**
   * How much room the content needs. A form reads best narrow; a table of
   * products does not fit in a form's width, so it asks for `wide`.
   */
  size?: "regular" | "wide";
  /**
   * Whether a click on the backdrop closes the dialog. Off by default, because
   * most dialogs hold a half-filled form; a dialog that only shows something
   * turns it on, since there is nothing a stray click could throw away.
   */
  dismissible?: boolean;
  onClose: () => void;
  children: JSX.Element;
};

/**
 * An editor that opens over the page instead of below it. The tree on the
 * catalog screen is long enough that an inline panel lands off-screen, and a
 * click that changes nothing you can see reads as a broken button.
 */
export const Modal = (props: ModalProps) => {
  const i18n = useI18n();
  let card: HTMLDivElement | undefined;

  createEffect(() => {
    if (!props.open) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") props.onClose();
    };
    const restore = document.body.style.overflow;
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";

    /*
     * Focus moves into the dialog, and back to whatever opened it once it goes,
     * so a keyboard reader lands on what just appeared and is not left on a
     * control the backdrop has covered.
     */
    const opener = document.activeElement;
    queueMicrotask(() => {
      if (card !== undefined && !card.contains(document.activeElement)) card.focus();
    });

    onCleanup(() => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = restore;
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    });
  });

  const onBackdrop = (event: MouseEvent) => {
    if (props.dismissible === true && event.target === event.currentTarget) props.onClose();
  };

  return (
    <Show when={props.open}>
      <Portal>
        {/*
          The backdrop does not dismiss unless the caller says so. A dialog
          usually holds a half-filled form, and a stray click beside it would
          throw the draft away with no warning, so leaving takes the close
          button or Escape.
        */}
        <div class={styles.backdrop} onMouseDown={onBackdrop}>
          <div
            ref={card}
            class={`${styles.card} ${props.size === "wide" ? styles.wide : ""}`}
            role="dialog"
            aria-modal="true"
            aria-label={props.title}
            tabIndex={-1}
          >
            <header class={styles.header}>
              <div class={styles.headline}>
                <h2 class={styles.title}>{props.title}</h2>
                <Show when={props.description !== undefined && props.description !== ""}>
                  <p class={styles.description}>{props.description}</p>
                </Show>
              </div>
              <IconButton
                label={i18n.t().common.cancel}
                icon={<CloseIcon />}
                onClick={() => props.onClose()}
              />
            </header>
            <div class={styles.body}>{props.children}</div>
          </div>
        </div>
      </Portal>
    </Show>
  );
};
